import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, TextField, Button, Typography, Alert, Paper, IconButton, InputAdornment } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../../contexts/AuthContext';
import { ColorsEnum } from 'common/theme/colors';
import Logo from "../../assets/logo-notext.png";

// The login page is always shown in English — there is no logged-in user
// yet to have an assigned language, and it must never pick up a stale
// language left in LanguageContext state from a previous session.
const REDIRECT_KEY = 'demo_redirect_in_progress';
const REDIRECT_TIMEOUT = 5000; // 5 seconds

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if user is already authenticated and redirect if returnUrl is present
  useEffect(() => {
    const returnUrl = searchParams.get('returnUrl');
    
    // Check if a redirect is already in progress (prevents loops)
    const redirectTimestamp = sessionStorage.getItem(REDIRECT_KEY);
    if (redirectTimestamp) {
      const elapsed = Date.now() - parseInt(redirectTimestamp, 10);
      if (elapsed < REDIRECT_TIMEOUT) {
        // Clear the returnUrl to break the loop
        if (returnUrl) {
          navigate('/login', { replace: true });
        }
        return;
      }
      // Redirect timed out, clear it
      sessionStorage.removeItem(REDIRECT_KEY);
    }

    if (user && returnUrl) {
      // User is already authenticated — redirect back to admin-frontend.
      // No token in the URL: admin-frontend silently re-authenticates via
      // the httpOnly refresh cookie shared on the API domain (issue #6).
      sessionStorage.setItem(REDIRECT_KEY, Date.now().toString());
      window.location.href = returnUrl;
    } else if (user && !returnUrl) {
      // User is authenticated but no returnUrl, go to dashboard
      sessionStorage.removeItem(REDIRECT_KEY);
      sessionStorage.removeItem('admin_redirect_in_progress');
      navigate('/');
    }
  }, [user, searchParams, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      
      // Check if there's a returnUrl (for admin-frontend redirect)
      const returnUrl = searchParams.get('returnUrl');
      if (returnUrl) {
        // Redirect back without a token in the URL — admin-frontend
        // re-authenticates silently via the httpOnly refresh cookie (issue #6).
        sessionStorage.setItem(REDIRECT_KEY, Date.now().toString());
        window.location.href = returnUrl;
        return;
      }
      
      // Normal flow - navigate to dashboard
      sessionStorage.removeItem(REDIRECT_KEY);
      sessionStorage.removeItem('admin_redirect_in_progress');
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        position: 'relative',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          padding: 4,
          maxWidth: 400,
          width: '100%',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          component="img"
          src={Logo}
          alt="Logo"
          sx={{ height: 30, marginBottom: "5px" }}
        />
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Sign In
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Welcome to the RPM Demo Dashboard
        </Typography>

        <form onSubmit={handleSubmit}>
          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            autoFocus
          />
          <TextField
            label="Password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword((visible) => !visible)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{ mt: 3, mb: 2, py: 1.5, backgroundColor: ColorsEnum.darkGrey, color: ColorsEnum.white }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default Login;
